import React, { Fragment, useEffect, useState } from 'react';
import { useResolvedPath } from 'react-router-dom';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  TabGroup,
  TabPanel,
  TabPanels,
  Transition,
} from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../widgets/Modal';
import FilterSidebar from '../FilterLayout/FilterSidebar';
import Button from '../../../widgets/Button';
import OrganisationFilter from './FilterOptions/OrganisationFilter';
import DepartmentFilter from './FilterOptions/DepartmentFilter';
import GenderFilter from './FilterOptions/GenderFilter';
import AgeFilter from './FilterOptions/AgeFilter';
import RegionFilter from './FilterOptions/RegionFilter';
import BottomSheet from '../../Layout/Header/BottomSheet/BottomSheet';
import useFilters from '../../../../utilities/hooks/useFilters';
import useOrganisationsList from '../../../../utilities/hooks/queryHooks/useOrganizationsList';
import { useWidgetsModalContext } from '../../../../shared/providers/WidgetsModalProvider';

import Chevron from '../../../../assets/icons/General/chevron.svg?react';

import {
  DeptQueryFunction,
  IData,
  MaybeSelfData,
  IOrganisation,
  RegionQueryFunction,
  ShowOrgFilter,
  SideBarItem,
} from '../../../../shared/types/filters';
import { FilterKeys } from '../../../../shared/constants/filter/filter';
import Loader from '../../../../assets/icons/loaderDark.gif';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import useAuth from '../../../../utilities/hooks/useAuth';

const renderCondition = (
  item: any,
  showOrgs: any,
  showRegion: any,
  showDept: any,
) =>
  (useResolvedPath('.').pathname === '/reports' && item.key !== 'filter_org') ||
  (!showOrgs && item.key === 'filter_org') ||
  (!showRegion && item.key === 'filter_region') ||
  (!showDept && item.key === 'filter_department');

export default function GeneralFiltersV2({ trial }: { trial?: boolean }) {
  const {
    showFilterModal,
    setShowFilterModal,
    showOrgFilter,
    setShowOrgFilter,
    showMobileFilterModal,
    setMobileShowFilterModal,
  } = useWidgetsModalContext();
  const { configData } = useFilterData();
  const { selfData, loadingSelf } = useAuth();
  const canListOrganisations = !!configData?.showOrganisationDropdown;

  const {
    resetFilter,
    selectedDepartments,
    selectedOrganisations,
    ageFilter,
    genderFilter,
    regionFilter,
    saveFilter,
    cancelFilter,
  } = useFilters();

  const { t } = useTranslation();

  const sidebarItems = [
    {
      label: t('organisation'),
      numberOfSelected: Array.from(selectedOrganisations.values()).filter(
        Boolean,
      ).length,
      key: FilterKeys.organisation,
    },
    {
      label: t('filterRegionTitle'),
      numberOfSelected: regionFilter.length,
      key: FilterKeys.region,
    },
    {
      label: t('filterDepartmentTitle'),
      numberOfSelected: Array.from(selectedDepartments.values()).filter(Boolean)
        .length,
      key: FilterKeys.department,
    },
    {
      label: t('filterGenderTitle'),
      numberOfSelected: genderFilter.length,
      key: FilterKeys.gender,
    },
    {
      label: t('filterAgeGroupTitle'),
      numberOfSelected: ageFilter.length,
      key: FilterKeys.age,
    },
  ];

  const {
    data: orgList,
    isLoading: isLoadingOrgList,
    refetch: refetchOrgList,
  } = useOrganisationsList(showOrgFilter, {
    enable: false,
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    if (!canListOrganisations || !Array.isArray(orgList)) return;

    const organisations = new Map<number, boolean>(
      orgList.map((organisation: { id: number }) => [organisation.id, true]),
    );
    setShowOrgFilter({
      organisationId: organisations,
      selectedOrganisations: organisations,
    });
  }, [canListOrganisations, orgList]);

  const hasCountries =
    (canListOrganisations &&
      Array.isArray(orgList) &&
      orgList.some(
        (item) =>
          item &&
          item.operationalCountries &&
          item.operationalCountries.length > 0,
      )) ||
    (configData && configData.countries.length > 0);

  const [queryDept, setQueryDept] = useState<IData>({ teams: [] });
  const [queryRegion, setQueryRegion] = useState<IData>({ countries: [] });
  const [numberOfAppliedFilters, setNumberOfAppliedFilters] = useState(0);

  const handleHide = () => setMobileShowFilterModal(false);

  const deptQuery: DeptQueryFunction = (depp) => {
    setQueryDept(depp);
  };

  const regionQuery: RegionQueryFunction = (reg) => {
    if (reg) setQueryRegion(reg);
  };

  const shouldRenderItem = (
    item: SideBarItem,
    selfData: MaybeSelfData,
    showOrgFilter: ShowOrgFilter,
    hasCountries?: boolean,
  ): boolean => {
    const showOrgs = canListOrganisations;
    const showRegion = hasCountries;
    const showDept = showOrgFilter.organisationId.size < 11;

    return renderCondition(item, showOrgs, showRegion, showDept);
  };

  const renderFilter = (
    item: SideBarItem,
    selfData: MaybeSelfData,
    showOrgFilter: ShowOrgFilter,
    orgList: IOrganisation[] = [],
    deptQuery: DeptQueryFunction,
    regionQuery: RegionQueryFunction,
    hasCountries?: boolean,
    countriesData?: { countries: any[] },
  ): React.ReactNode => {
    switch (item.key) {
      case FilterKeys.organisation:
        return (
          canListOrganisations && (
            <OrganisationFilter
              data={orgList?.sort((a, b) => a.name.localeCompare(b.name)) || []}
              callBackDept={deptQuery}
              callBackRegion={regionQuery}
            />
          )
        );
      case FilterKeys.region:
        return (
          ((selfData?.data?.config &&
            !canListOrganisations &&
            countriesData?.countries?.length !== 0) ||
            (selfData?.data?.config && canListOrganisations && hasCountries)) &&
          queryRegion.countries && <RegionFilter orgs={queryRegion.countries} />
        );

      case FilterKeys.department:
        return (
          showOrgFilter?.organisationId.size < 11 && (
            <DepartmentFilter
              orgs={queryDept.teams}
              organisations={canListOrganisations ? orgList : undefined}
            />
          )
        );
      case FilterKeys.gender:
        return (
          <GenderFilter
            customGender={
              !!(
                selfData?.data?.config &&
                selfData?.data?.config.BASIC_GENDER_OPTIONS_ONLY === 'true'
              )
            }
          />
        );
      case FilterKeys.age:
        return <AgeFilter />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const totalSelected =
      ageFilter.length +
      genderFilter.length +
      regionFilter.length +
      Array.from(selectedDepartments.values()).filter(Boolean).length +
      Array.from(selectedOrganisations.values()).filter(Boolean).length;

    setNumberOfAppliedFilters(totalSelected);
  }, [
    selectedOrganisations,
    selectedDepartments,
    regionFilter,
    genderFilter,
    ageFilter,
  ]);

  useEffect(() => {
    if (!loadingSelf && canListOrganisations) {
      refetchOrgList();
    }
  }, [loadingSelf, canListOrganisations]);

  return (
    <>
      <BottomSheet show={showMobileFilterModal} onHide={handleHide}>
        <div className="mb-[88.67px] max-h-[85vh] overflow-auto px-6 py-[70px] pb-2">
          <div className="text-black-900 absolute top-0 left-0 z-20 w-full bg-white pt-[30px] pb-6 pl-6 text-lg font-semibold">
            {t('FILTERS')}
          </div>
          {sidebarItems.map((item) => {
            if (shouldRenderItem(item, selfData, showOrgFilter, hasCountries)) {
              return null;
            }

            return (
              <Disclosure as="div" key={`sidebar-item-${item.label}`}>
                {({ open }) => (
                  <>
                    {/* Disclosure Button */}
                    <DisclosureButton
                      as="div"
                      className="text-black-600 cursor-pointer px-4 py-4 font-semibold transition-all duration-200"
                      title={item.label}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <h2>{item.label}</h2>
                          {item.numberOfSelected > 0 && (
                            <span className="bg-black-300 text-black-600 flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold">
                              {item.numberOfSelected}
                            </span>
                          )}
                        </div>
                        <Chevron
                          stroke="var(--color-black-400)"
                          className={`transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
                        />
                      </div>
                    </DisclosureButton>

                    {/* Animated Disclosure Panel */}
                    <Transition
                      show={open}
                      enter="transition-all duration-300 ease-out"
                      enterFrom="opacity-0 max-h-0"
                      enterTo="opacity-100 max-h-[500px]"
                      leave="transition-all duration-300 ease-in"
                      leaveFrom="opacity-100 max-h-[500px]"
                      leaveTo="opacity-0 max-h-0"
                    >
                      <DisclosurePanel
                        as="div"
                        className="ml-4 overflow-hidden"
                      >
                        <div className="content-inner">
                          {renderFilter(
                            item,
                            selfData,
                            showOrgFilter,
                            orgList,
                            deptQuery,
                            regionQuery,
                            hasCountries,
                          )}
                        </div>
                      </DisclosurePanel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            );
          })}
        </div>

        <div className="border-black-200 absolute bottom-0 m-auto flex w-full justify-center border-t py-6">
          <Button
            className="w-full max-w-[90%]"
            onClick={() => {
              handleHide();
              saveFilter();
            }}
          >
            {t('APPLY')}{' '}
            {numberOfAppliedFilters > 0 && (
              <>
                {numberOfAppliedFilters}
                {numberOfAppliedFilters === 1 ? ' filter' : ' filters'}
              </>
            )}
          </Button>
        </div>
      </BottomSheet>

      <Modal
        open={showFilterModal}
        handleClose={() => {
          setShowFilterModal(false);
          cancelFilter();
        }}
        modalClassName="!p-0"
        closeButton
      >
        <h3 className="border-black-200 text-black-900 border-b px-[17px] py-5 text-2xl font-semibold capitalize">
          {t('FILTERS')}
        </h3>
        <div
          className={`px-4 ${
            (loadingSelf || isLoadingOrgList) && '!flex justify-center'
          } divide-taupe-550 grid h-[65vh] w-[70vw] divide-x ${
            (!loadingSelf || !isLoadingOrgList) && 'grid-cols-4'
          }`}
        >
          {loadingSelf ? (
            <img src={Loader} className="h-10 w-10" alt="" />
          ) : (
            <TabGroup as={Fragment} vertical>
              <FilterSidebar
                showOrgs={canListOrganisations}
                showRegion={hasCountries}
                showDept={showOrgFilter.organisationId.size < 11}
                sidebarItems={sidebarItems}
              />
              <div className="col-span-3 flex flex-col">
                <TabPanels className="h-full grow [&>div]:outline-hidden">
                  {canListOrganisations && (
                    <TabPanel>
                      <OrganisationFilter
                        data={
                          orgList &&
                          orgList.sort((a: any, b: any) =>
                            a.name.localeCompare(b.name),
                          )
                        }
                        callBackDept={deptQuery}
                        callBackRegion={regionQuery}
                      />
                    </TabPanel>
                  )}

                  {((!canListOrganisations &&
                    configData?.countries?.length !== 0) ||
                    (canListOrganisations && hasCountries)) && (
                    <TabPanel>
                      {queryRegion.countries && (
                        <RegionFilter orgs={queryRegion.countries} />
                      )}
                    </TabPanel>
                  )}
                  {showOrgFilter.organisationId.size < 11 && (
                    <TabPanel>
                      <DepartmentFilter
                        orgs={queryDept.teams}
                        organisations={
                          canListOrganisations ? orgList : undefined
                        }
                      />
                    </TabPanel>
                  )}

                  <TabPanel>
                    <GenderFilter
                      customGender={
                        selfData?.data?.config.BASIC_GENDER_OPTIONS_ONLY ===
                        'true'
                      }
                    />
                  </TabPanel>
                  <TabPanel>
                    <AgeFilter />
                  </TabPanel>
                </TabPanels>
              </div>
            </TabGroup>
          )}
        </div>
        <div className="border-black-200 flex w-full items-center justify-end gap-5 border-t px-5 py-2">
          {trial && (
            <p className="mr-auto ml-0 text-base text-red-500">
              {t('FILTERS_SAMPLE_DATA_NOTE')}
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => {
              resetFilter();
            }}
            disabled={loadingSelf}
          >
            {t('filterResetAll')}
          </Button>
          <Button
            onClick={() => {
              saveFilter();
              setShowFilterModal(false);
            }}
            disabled={loadingSelf}
          >
            {t('filterApply')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
